import { useEffect, useRef } from "react";

/**
 * Custom hook that executes a callback after an initial delay,
 * then repeatedly at regular intervals while loading continues
 * 
 * @param isLoading - Whether the loading state is active
 * @param callback - Function to execute on timeout/interval
 * @param initialDelay - Initial delay in milliseconds (default: 45000)
 * @param recurringInterval - Recurring interval in milliseconds (default: 10000)
 */
export function useLoadingTimeout(
    isLoading: boolean,
    callback: () => void,
    initialDelay: number = 45000,
    recurringInterval: number = 10000
) {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number | null>(null);
    const callbackRef = useRef(callback);

    // Keep callback ref up to date
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        // Clear and reset when not loading
        if (!isLoading) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            startTimeRef.current = null;
            return;
        }

        // Only set up timer if we don't have one already
        if (timerRef.current) {
            return;
        }

        // Record start time
        startTimeRef.current = Date.now();

        // Initial timeout
        timerRef.current = setTimeout(() => {
            callbackRef.current();

            // Set up recurring interval
            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - (startTimeRef.current || Date.now());
                callbackRef.current();
            }, recurringInterval);
        }, initialDelay);

        // Cleanup
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isLoading, initialDelay, recurringInterval]);
}